import { BorrowerType, TrusteeType } from '@/helpers/const';
import { BorrowerRole } from '@/types/BorrowerGroup';
import type { BorrowerGroup } from '@/types/BorrowerGroup';
import type { LoanApplication } from '@/types';

/**
 * Structured company/trust borrower groups for the submit payload — the native replacement for the
 * temporary `commercialBorrowerData` plain-text note. The backend creates a real Company/Trust
 * Borrower per group and attaches each person as a BorrowerApplicant with the role supplied here.
 *
 * People are referenced by their position (`applicantIndex`) in the flat `applicants` array, which
 * the backend persists in the same order — submit applicants carry no stable id.
 */
export interface SubmitBorrowerGroupApplicant {
  applicantIndex: number;
  role: string;
}

export interface SubmitBorrowerCompanyDetails {
  companyName?: string;
  hasRegisteredABN?: boolean;
  abnAcnNumber?: string;
  isTrading?: boolean;
}

export interface SubmitBorrowerTrustDetails {
  trustName?: string;
  trustStructure?: string;
  companyDetails?: SubmitBorrowerCompanyDetails;
}

export interface SubmitBorrowerGroup {
  id?: string;
  type: string;
  entityName?: string;
  companyDetails?: SubmitBorrowerCompanyDetails;
  trustDetails?: SubmitBorrowerTrustDetails;
  applicants: SubmitBorrowerGroupApplicant[];
}

/** Maps a frontend per-group borrower type to the backend `BorrowerType` enum value. */
function mapGroupBorrowerType(group: BorrowerGroup): string | undefined {
  switch (group.type) {
    case BorrowerType.INDIVIDUAL:
      return 'individual';
    case BorrowerType.COMPANY:
      return 'company';
    case BorrowerType.TRUST:
      // A trust's backend type depends on whether its trustee is a company (corporate trust) or a
      // person (individual trust). A corporate trust is a single borrower holding both trust and
      // trustee-company fields.
      return group.trustDetails?.trusteeType === TrusteeType.COMPANY ? 'corporate_trust' : 'individual_trust';
    default:
      return undefined;
  }
}

function mapCompanyDetails(companyDetails: BorrowerGroup['companyDetails']): SubmitBorrowerCompanyDetails | undefined {
  if (!companyDetails) return undefined;
  return {
    companyName: companyDetails.companyName,
    hasRegisteredABN: companyDetails.hasRegisteredABN,
    abnAcnNumber: companyDetails.abnAcnNumber,
    isTrading: companyDetails.isTrading,
  };
}

/**
 * Resolves the roles to attach a person under for a given group. Every group uses the role the
 * borrower-details step already tagged — a person assigned once holds exactly one role — falling
 * back to a sensible per-type default only when no role was tagged.
 */
function resolveRoles(group: BorrowerGroup, role: string | undefined): string[] {
  if (role) return [role];
  switch (group.type) {
    case BorrowerType.COMPANY:
      return [BorrowerRole.DIRECTOR];
    case BorrowerType.TRUST:
      // A corporate trust's people are the trustee company's directors; only an individual-trustee
      // trust attaches a trustee. Mirrors how the borrower-details step collects them.
      return group.trustDetails?.trusteeType === TrusteeType.COMPANY ? [BorrowerRole.DIRECTOR] : [BorrowerRole.TRUSTEE];
    default:
      return [BorrowerRole.INDIVIDUAL];
  }
}

/**
 * Builds the structured `borrowerGroups` submit payload from the borrower groups the UI holds.
 * Returns `undefined` for individual-only applications so the backend keeps its single-individual
 * borrower behaviour unchanged.
 */
export function buildBorrowerGroups(data: LoanApplication): SubmitBorrowerGroup[] | undefined {
  const groups = data.borrowerGroups ?? [];
  const hasNonIndividual = groups.some((group) => group.type !== BorrowerType.INDIVIDUAL);
  if (!hasNonIndividual) return undefined;

  const applicantIndexById = new Map<string, number>();
  (data.applicants ?? []).forEach((applicant, index) => applicantIndexById.set(applicant.id, index));

  const submitGroups: SubmitBorrowerGroup[] = [];

  for (const group of groups) {
    const backendType = mapGroupBorrowerType(group);
    if (!backendType) continue;

    const trusteeCompanyDetails = mapCompanyDetails(group.trustDetails?.companyDetails);
    const trustDetails: SubmitBorrowerTrustDetails | undefined = group.trustDetails
      ? {
          trustName: group.trustDetails.trustName,
          trustStructure: group.trustDetails.trustStructure,
          ...(trusteeCompanyDetails ? { companyDetails: trusteeCompanyDetails } : {}),
        }
      : undefined;

    // Dedupe on (applicantIndex, role) so a person can legitimately hold two roles without
    // producing duplicate rows the backend's unique role index would reject.
    const seen = new Set<string>();
    const applicants: SubmitBorrowerGroupApplicant[] = [];
    for (const groupApplicant of group.applicants ?? []) {
      const applicantIndex = applicantIndexById.get(groupApplicant.applicantId);
      if (applicantIndex === undefined) continue;
      for (const role of resolveRoles(group, groupApplicant.role)) {
        const key = `${applicantIndex}|${role}`;
        if (seen.has(key)) continue;
        seen.add(key);
        applicants.push({ applicantIndex, role });
      }
    }

    // A group with no resolvable applicants would create a borrower entity with nobody attached;
    // drop it rather than persist an empty borrower.
    if (!applicants.length) continue;

    submitGroups.push({
      id: group.id,
      type: backendType,
      entityName: group.entityName,
      ...(mapCompanyDetails(group.companyDetails) ? { companyDetails: mapCompanyDetails(group.companyDetails) } : {}),
      ...(trustDetails ? { trustDetails } : {}),
      applicants,
    });
  }

  return submitGroups.length ? submitGroups : undefined;
}
